import React, { useCallback, useEffect, useMemo, useState } from "react"

import { useOrganization } from "@clerk/nextjs"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  Tabs,
  TabsList,
  TabsTrigger,
  type ColumnDef,
} from "@coval/ui"
import { ClipboardList, ShieldAlert, UserSearch } from "lucide-react"

import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin"
import { useQueryParams } from "@/hooks/useQueryParams"
import { calculatePresetDateRange } from "@/utils/date/dateRange"

import { useDashboardFilters } from "../../contexts/DashboardFiltersContext"
import { useDashboardBatchTransportQuery } from "../../routes/dashboardBatchTransportQuery"
import type { ChartDataPoint } from "../../types/chart"
import { DEFAULT_WIDGET_DATE_RANGE } from "../../types/dateRange"
import {
  getDashboardBatchRows,
  isTransportDashboardQueryLoading,
} from "../../utils/transportDashboardQueries"
import { BaseWidget, type WidgetProps } from "../BaseWidget"

type ViewMode = "by-metric" | "by-unique-call"

interface CallHistoryRow {
  annotation_id: string
  simulation_output_id: string
  project_name: string | null
  metric_name: string | null
  last_updated_at: string
  ground_truth_label: string | null
}

interface UniqueCallRow {
  simulation_output_id: string
  project_name: string | null
  metricsReviewed: number
  lastUpdatedAt: string
}

const ASSIGNEE_QUERY_STALE_TIME_MS = 5 * 60 * 1000
const ASSIGNEE_QUERY_GC_TIME_MS = 15 * 60 * 1000

const formatTimestamp = (iso: string): string => {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const TRUNCATED_ID_LENGTH = 8

const truncateId = (id: string): string =>
  id.length > TRUNCATED_ID_LENGTH ? `${id.slice(0, TRUNCATED_ID_LENGTH)}…` : id

const renderCallId = (row: { simulation_output_id: string }) => (
  <span title={row.simulation_output_id}>
    {truncateId(row.simulation_output_id)}
  </span>
)

const BY_METRIC_COLUMNS: ColumnDef<CallHistoryRow>[] = [
  {
    id: "simulation_output_id",
    header: "ID",
    width: 96,
    flex: false,
    cell: renderCallId,
  },
  {
    id: "project_name",
    header: "Project",
    cell: (row) => row.project_name ?? "—",
  },
  {
    id: "metric_name",
    header: "Metric",
    cell: (row) => row.metric_name ?? "—",
  },
  {
    id: "last_updated_at",
    header: "Reviewed At",
    width: 180,
    cell: (row) => formatTimestamp(row.last_updated_at),
  },
  {
    id: "ground_truth_label",
    header: "Label",
    cell: (row) => row.ground_truth_label ?? "—",
  },
]

const BY_UNIQUE_CALL_COLUMNS: ColumnDef<UniqueCallRow>[] = [
  {
    id: "simulation_output_id",
    header: "ID",
    width: 96,
    flex: false,
    cell: renderCallId,
  },
  {
    id: "project_name",
    header: "Project",
    cell: (row) => row.project_name ?? "—",
  },
  {
    id: "metrics_reviewed",
    header: "Metrics Reviewed",
    cell: (row) => row.metricsReviewed,
  },
  {
    id: "last_updated_at",
    header: "Last Reviewed At",
    width: 180,
    cell: (row) => formatTimestamp(row.lastUpdatedAt),
  },
]

const getSimulationLabel = (row: { simulation_output_id: string }) =>
  `Open simulation ${row.simulation_output_id} in new tab`

const AssigneeCallHistoryContent: React.FC<{
  onExportDataReady?: WidgetProps["onExportDataReady"]
}> = ({ onExportDataReady }) => {
  const isAdmin = useIsOrgAdmin()
  const { organization } = useOrganization()
  const queryParams = useQueryParams()
  const { state } = useDashboardFilters()
  const {
    dateRange: globalDateRange,
    globalDateRangeOverride,
    isOverrideActive,
  } = state

  const effectiveDateRange = useMemo(() => {
    if (isOverrideActive && globalDateRangeOverride) {
      return globalDateRangeOverride
    }
    if (globalDateRange) {
      return globalDateRange
    }
    return calculatePresetDateRange(DEFAULT_WIDGET_DATE_RANGE.preset!)
  }, [globalDateRange, globalDateRangeOverride, isOverrideActive])

  const [selectedAssignee, setSelectedAssignee] = useState<string>("")
  const [viewMode, setViewMode] = useState<ViewMode>("by-metric")

  const handleAssigneeChange = useCallback((value: string) => {
    setSelectedAssignee(value)
  }, [])

  const handleViewModeChange = useCallback((value: string) => {
    setViewMode(value as ViewMode)
  }, [])

  const assigneeVolumeQueryEnabled = isAdmin && Boolean(effectiveDateRange)
  const assigneeVolumeQuery = useDashboardBatchTransportQuery({
    request: {
      queries: [
        {
          query_id: "assignee_call_volume",
          kind: "queryAssigneeCallVolume",
          start_date: effectiveDateRange?.start?.toISOString(),
          end_date: effectiveDateRange?.end?.toISOString(),
        },
      ],
    },
    options: {
      enabled: assigneeVolumeQueryEnabled,
      staleTime: ASSIGNEE_QUERY_STALE_TIME_MS,
      gcTime: ASSIGNEE_QUERY_GC_TIME_MS,
    },
  })
  const assigneeData = useMemo(
    () =>
      getDashboardBatchRows<{
        assignee: string
      }>(assigneeVolumeQuery.data),
    [assigneeVolumeQuery.data],
  )
  const isLoadingAssignees = isTransportDashboardQueryLoading(
    assigneeVolumeQuery,
    assigneeVolumeQueryEnabled,
  )

  const assigneeOptions = useMemo(() => {
    if (!assigneeData) {
      return []
    }
    return assigneeData.map((row) => row.assignee).sort()
  }, [assigneeData])

  useEffect(() => {
    if (!selectedAssignee && assigneeOptions.length > 0) {
      setSelectedAssignee(assigneeOptions[0])
    }
  }, [assigneeOptions, selectedAssignee])

  const assigneeHistoryQueryEnabled =
    isAdmin && Boolean(selectedAssignee) && Boolean(effectiveDateRange)
  const assigneeHistoryQuery = useDashboardBatchTransportQuery({
    request: {
      queries: [
        {
          query_id: "assignee_call_history",
          kind: "queryAssigneeCallHistory",
          assignee: selectedAssignee,
          start_date: effectiveDateRange?.start?.toISOString(),
          end_date: effectiveDateRange?.end?.toISOString(),
        },
      ],
    },
    options: {
      enabled: assigneeHistoryQueryEnabled,
      staleTime: ASSIGNEE_QUERY_STALE_TIME_MS,
      gcTime: ASSIGNEE_QUERY_GC_TIME_MS,
    },
  })
  const callHistory = useMemo(
    () => getDashboardBatchRows<CallHistoryRow>(assigneeHistoryQuery.data),
    [assigneeHistoryQuery.data],
  )
  const isLoadingHistory = isTransportDashboardQueryLoading(
    assigneeHistoryQuery,
    assigneeHistoryQueryEnabled,
  )
  const historyError = assigneeHistoryQuery.error

  const uniqueCallRows = useMemo(() => {
    if (!callHistory) {
      return []
    }
    const map = new Map<string, UniqueCallRow>()
    for (const row of callHistory) {
      const existing = map.get(row.simulation_output_id)
      if (existing) {
        existing.metricsReviewed += 1
        if (row.last_updated_at > existing.lastUpdatedAt) {
          existing.lastUpdatedAt = row.last_updated_at
        }
      } else {
        map.set(row.simulation_output_id, {
          simulation_output_id: row.simulation_output_id,
          project_name: row.project_name,
          metricsReviewed: 1,
          lastUpdatedAt: row.last_updated_at,
        })
      }
    }
    return Array.from(map.values())
  }, [callHistory])

  const openSimulation = useCallback(
    (row: { simulation_output_id: string }) => {
      if (!organization?.slug) {
        return
      }
      window.open(
        queryParams.getHref(
          `/simulations/${row.simulation_output_id}`,
          organization.slug,
        ),
        "_blank",
      )
    },
    [organization?.slug, queryParams],
  )

  const exportChartData: ChartDataPoint[] = useMemo(() => {
    if (!callHistory || callHistory.length === 0) {
      return []
    }
    return callHistory.map((row) => ({
      date: `${row.simulation_output_id} | ${row.project_name ?? "—"} | ${row.ground_truth_label ?? "—"}`,
      timestamp: new Date(row.last_updated_at).getTime(),
      bucketTime: row.last_updated_at,
      value: 1,
    }))
  }, [callHistory])

  const prevExportRef = React.useRef<string>("")
  React.useEffect(() => {
    if (!onExportDataReady) {
      return
    }

    const key = JSON.stringify({
      assignee: selectedAssignee,
      rows: exportChartData.length,
      start: effectiveDateRange?.start?.toISOString(),
      end: effectiveDateRange?.end?.toISOString(),
    })
    if (prevExportRef.current === key) {
      return
    }
    prevExportRef.current = key

    if (exportChartData.length > 0) {
      onExportDataReady({
        widgetType: "chart",
        chartData: exportChartData,
        splitKeys: [],
        displayNameLookups: null,
        dateRange: effectiveDateRange
          ? { start: effectiveDateRange.start, end: effectiveDateRange.end }
          : undefined,
      })
    } else {
      onExportDataReady(null)
    }
  }, [exportChartData, effectiveDateRange, onExportDataReady, selectedAssignee])

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <ShieldAlert className="w-8 h-8 mx-auto mb-2" />
          <p>Admin access required</p>
        </div>
      </div>
    )
  }

  const hasHistory = callHistory.length > 0

  return (
    <div className="flex flex-col h-full gap-3 p-3 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <Select
            value={selectedAssignee}
            onValueChange={handleAssigneeChange}
            disabled={isLoadingAssignees}
          >
            <SelectTrigger aria-label="Select assignee" variant="secondary">
              <SelectValue
                placeholder={
                  isLoadingAssignees
                    ? "Loading assignees…"
                    : "Select an assignee"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {assigneeOptions.map((email) => (
                <SelectItem key={email} value={email}>
                  {email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedAssignee && !isLoadingHistory && hasHistory && (
          <div className="min-w-0 shrink-0">
            <Tabs value={viewMode} onValueChange={handleViewModeChange}>
              <TabsList variant="pill" aria-label="View mode">
                <TabsTrigger value="by-metric" variant="pill">
                  By Metric
                </TabsTrigger>
                <TabsTrigger value="by-unique-call" variant="pill">
                  By Unique Call
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}
      </div>

      {!selectedAssignee && !isLoadingAssignees && (
        <div className="flex items-center justify-center flex-1 text-coval-text-muted">
          <div className="text-center">
            <UserSearch className="w-8 h-8 mx-auto mb-2" />
            <p>Select an assignee to view their call history</p>
          </div>
        </div>
      )}

      {selectedAssignee &&
        !isLoadingHistory &&
        historyError &&
        callHistory.length === 0 && (
          <div className="flex items-center justify-center flex-1 text-coval-text-muted">
            <div className="text-center">
              <ClipboardList className="w-8 h-8 mx-auto mb-2" />
              <p>Error loading call history</p>
            </div>
          </div>
        )}

      {selectedAssignee &&
        !isLoadingHistory &&
        !historyError &&
        callHistory.length === 0 && (
          <div className="flex items-center justify-center flex-1 text-coval-text-muted">
            <div className="text-center">
              <ClipboardList className="w-8 h-8 mx-auto mb-2" />
              <p>No calls found for this assignee in the selected date range</p>
            </div>
          </div>
        )}

      {selectedAssignee && (isLoadingHistory || hasHistory) && (
        <>
          <div className="flex-1 min-h-0">
            {viewMode === "by-metric" ? (
              <Table<CallHistoryRow>
                aria-label="Assignee call history by metric"
                data={callHistory}
                columns={BY_METRIC_COLUMNS}
                getRowId={(row) => row.annotation_id}
                onRowClick={openSimulation}
                rowClickDescription="Press Enter to open in a new tab"
                rowAttributes={(row) => ({
                  "aria-label": getSimulationLabel(row),
                })}
                emptyState="No calls found for this assignee in the selected date range"
                isLoading={isLoadingHistory}
                sortStorageKey={null}
                edgeToEdge
                className="h-full"
              />
            ) : (
              <Table<UniqueCallRow>
                aria-label="Assignee call history by unique call"
                data={uniqueCallRows}
                columns={BY_UNIQUE_CALL_COLUMNS}
                getRowId={(row) => row.simulation_output_id}
                onRowClick={openSimulation}
                rowClickDescription="Press Enter to open in a new tab"
                rowAttributes={(row) => ({
                  "aria-label": getSimulationLabel(row),
                })}
                emptyState="No calls found for this assignee in the selected date range"
                isLoading={isLoadingHistory}
                sortStorageKey={null}
                edgeToEdge
                className="h-full"
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

export class AssigneeCallHistoryWidget extends BaseWidget {
  type = "assignee_call_history"
  title = "Assignee Call History"
  description = "Drill-down view of individual calls handled by an assignee"
  icon = (<UserSearch className="w-5 h-5" />)
  disabled = false

  getMinimumDimensions() {
    return { minW: 12, minH: 8 }
  }

  renderWidget(props: WidgetProps): React.ReactNode {
    return (
      <div className="w-full h-full select-none">
        <AssigneeCallHistoryContent
          onExportDataReady={props.onExportDataReady}
        />
      </div>
    )
  }

  getDefaultConfig() {
    return {
      title: "Assignee Call History",
      description: "Drill-down view of individual calls handled by an assignee",
    }
  }
}
